import type { ReactElement } from 'react'
import type { ImportEndpointsRequest } from '../../../types/case-container'
import {
  CsvImportBlockingErrorsStep,
  CsvImportConfirmFixesStep,
  CsvImportPreviewStep,
  CsvImportSuccessStep,
} from './CsvImportSteps'
import {
  ApiErrorBanner,
  ImportingStep,
  ModalHeader,
  SelectStep,
} from './csv-import-modal/csv-import-modal.parts'
import { type ImportResult, useCsvImportModal } from './csv-import-modal/use-csv-import-modal'

interface CsvImportModalProps {
  caseId: string
  onClose: () => void
  onImport: (data: ImportEndpointsRequest) => Promise<ImportResult | null>
  importing: boolean
  apiError?: string | null
}

export function CsvImportModal(props: CsvImportModalProps): ReactElement {
  const { caseId, onClose, onImport, apiError } = props
  const state = useCsvImportModal({ caseId, onImport })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <ModalHeader onClose={onClose} />
        {apiError && <ApiErrorBanner apiError={apiError} />}
        {state.step === 'select' && (
          <SelectStep
            fileInputRef={state.fileInputRef}
            handleFileChange={state.handleFileChange}
            onClose={onClose}
          />
        )}
        {state.step === 'blocking-errors' && (
          <CsvImportBlockingErrorsStep
            blockingErrors={state.blockingErrors}
            onDownloadLog={state.downloadErrorLog}
            onReset={state.handleReset}
            onClose={onClose}
          />
        )}
        {state.step === 'confirm-fixes' && (
          <CsvImportConfirmFixesStep
            fixableWarnings={state.fixableWarnings}
            preview={state.preview}
            totalEndpoints={state.allParsedEndpoints.length}
            onReset={state.handleReset}
            onImport={() => {
              void state.handleImport()
            }}
          />
        )}
        {state.step === 'preview' && (
          <CsvImportPreviewStep
            preview={state.preview}
            totalEndpoints={state.allParsedEndpoints.length}
            error={state.error}
            onReset={state.handleReset}
            onImport={() => {
              void state.handleImport()
            }}
          />
        )}
        {state.step === 'importing' && <ImportingStep count={state.allParsedEndpoints.length} />}
        {state.step === 'success' && state.result && (
          <CsvImportSuccessStep
            result={state.result}
            verificationRecords={state.verificationRecords}
            loadingVerification={state.loadingVerification}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  )
}
